import React from 'react';
import { motion } from 'framer-motion';
import { User, Brain, Laptop } from 'lucide-react';

const servicos = [
  {
    icon: User,
    title: 'Atendimento individual',
    description:
      'Sessões de escuta clínica para adolescentes e adultos. Cada processo é conduzido no seu ritmo, com espaço para elaborar o que você traz, sem julgamentos e sem respostas prontas.',
  },
  {
    icon: Brain,
    title: 'Abordagem psicanal\u00edtica',
    description:
      'A psicanálise busca compreender os conflitos, os padrões que se repetem e os aspectos da história de cada pessoa que influenciam seu modo de sentir, pensar e se relacionar. Esse processo favorece o autoconhecimento e possibilita transformações mais duradouras.',
  },
  {
    icon: Laptop,
    title: 'Online e presencial',
    description:
      'Atendimento no consultório em Caieiras\u2013SP ou online, com a mesma qualidade cl\u00ednica e \u00e9tica profissional. Voc\u00ea escolhe o formato que funciona melhor para a sua rotina.',
  },
];

const categorias = [
  {
    label: 'Emo\u00e7\u00f5es e humor',
    items: [
      'Ansiedade',
      'Ang\u00fastia',
      'Depress\u00e3o',
      'Altera\u00e7\u00e3o de Humor',
      'Estresse',
      'Raiva',
      'S\u00edndrome do P\u00e2nico',
    ],
  },
  {
    label: 'Relacionamentos',
    items: [
      'Conflitos Familiares',
      'Depend\u00eancia Emocional',
      'Relacionamento Abusivo',
      'Relacionamentos',
    ],
  },
  {
    label: 'Autodesenvolvimento',
    items: [
      'Autoconhecimento',
      'Desenvolvimento Pessoal',
      'Inseguran\u00e7a',
      'Procrastina\u00e7\u00e3o',
    ],
  },
  {
    label: 'Traumas e viv\u00eancias',
    items: [
      'Traumas',
      'Acompanhamento Psicol\u00f3gico',
      'Adolescentes',
      'Adultos',
    ],
  },
];

const ServicesSection: React.FC = () => {
  return (
    <motion.section
      id="servicos"
      className="py-20 bg-[#f3f6fb]"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Cabecalho */}
        <div className="text-center mb-12">
          <span className="inline-flex rounded-full bg-white px-4 py-2 text-xs tracking-[0.16em] uppercase text-[#2a3c5c] font-medium border border-[#d7deeb]">
            Especialidades
          </span>
          <motion.h2
            className="text-3xl sm:text-4xl font-semibold mt-5 mb-4 text-[#2a3c5c]"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {'Cuidado psicol\u00f3gico para diferentes fases e desafios'}
          </motion.h2>
          <motion.p
            className="text-gray-600 max-w-3xl mx-auto text-lg"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
          >
            {'Atendimento psicológico para adolescentes e adultos, com abordagem psicanalítica e foco no acolhimento, na elaboração das experiências e na transformação subjetiva.'}
          </motion.p>
        </div>

        {/* 3 cards descritivos */}
        <div id="como-funciona" className="grid md:grid-cols-3 gap-6 mb-10">
          {servicos.map((servico, index) => {
            const Icon = servico.icon;
            return (
              <motion.div
                key={servico.title}
                className="group rounded-2xl border border-[#d7deeb] bg-white p-7 shadow-[0_10px_35px_rgba(26,43,73,0.08)] flex flex-col"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{
                  y: -8,
                  boxShadow: '0 24px 55px rgba(26,43,73,0.15)',
                  borderColor: '#afc2dc',
                  transition: { duration: 0.25, ease: 'easeOut' },
                }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                viewport={{ once: true }}
              >
                <div className="h-12 w-12 rounded-full bg-[#edf2fb] text-[#2a3c5c] flex items-center justify-center mb-5 group-hover:bg-[#2a3c5c] group-hover:text-white transition-colors duration-300">
                  <Icon size={22} />
                </div>
                <h3 className="text-xl font-semibold text-[#2a3c5c] mb-3">{servico.title}</h3>
                <p className="text-gray-600 leading-relaxed flex-1">{servico.description}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Card de areas de atuacao */}
        <motion.div
          className="rounded-2xl border border-[#d7deeb] bg-white p-6 sm:p-8 shadow-[0_15px_45px_rgba(26,43,73,0.08)] mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          whileHover={{
            boxShadow: '0 24px 60px rgba(26,43,73,0.13)',
            borderColor: '#c5d4e8',
            transition: { duration: 0.3 },
          }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl font-semibold text-[#2a3c5c] mb-2">
            {'Voc\u00ea est\u00e1 no lugar certo se busca apoio para'}
          </h3>
          <p className="text-gray-500 mb-8 text-sm">
            {'Algumas das \u00e1reas em que o acompanhamento psicol\u00f3gico pode fazer diferen\u00e7a'}
          </p>

          <div className="grid sm:grid-cols-2 gap-6">
            {categorias.map((categoria, catIndex) => (
              <motion.div
                key={categoria.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: catIndex * 0.1 }}
                viewport={{ once: true }}
              >
                <p className="text-xs font-semibold tracking-[0.14em] uppercase text-[#2a3c5c]/60 mb-3">
                  {categoria.label}
                </p>
                <div className="flex flex-wrap gap-2">
                  {categoria.items.map((item) => (
                    <span
                      key={item}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-[#e2e7f2] bg-[#f9fbff] px-3 py-1.5 text-sm text-[#243452] transition-all duration-200 hover:bg-[#edf2fb] hover:border-[#b8cadf] hover:text-[#1a2a48] select-none cursor-default"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-[#2a3c5c]/40 flex-shrink-0" />
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 border-t border-[#e2e7f2] pt-6 flex items-center gap-4">
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-[0.14em] text-[#2a3c5c]/60 mb-1">Abordagem</h4>
              <p className="text-[#2a3c5c] font-semibold text-lg">{'Psicanálise'}</p>
            </div>
            <div className="w-px h-10 bg-[#e2e7f2]" />
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-[0.14em] text-[#2a3c5c]/60 mb-1">{'P\u00fablico'}</h4>
              <p className="text-[#2a3c5c] font-semibold text-lg">{'Adolescentes e Adultos'}</p>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <div className="text-center">
          <a
            href="#contato"
            className="inline-flex items-center justify-center rounded-lg bg-[#2a3c5c] px-7 py-3 text-white font-semibold transition duration-300 hover:bg-[#1f2f4c]"
          >
            Agendar minha primeira conversa
          </a>
        </div>

      </div>
    </motion.section>
  );
};

export default ServicesSection;
